import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

import {
  createCountdown,
  createEvent,
  createTask,
  deleteCountdown,
  deleteEvent,
  deleteTask,
  getMyEntitlement,
  getMyProfile,
  getPreferences,
  listCountdowns,
  listEvents,
  listRoutineCompletions,
  listTasks,
  savePreferences,
  setRoutineCompleted,
  setTaskCompleted,
  submitFeedback,
  type CountdownRow,
  type EntitlementRow,
  type EventRow,
  type ProfileIdentity,
  type RoutineCompletionRow,
  type TaskRow,
  type UserPreferencesRow,
} from "../lib/webAppApi";

function errorMessage(error: unknown) {
  if (error instanceof Error) {
    return error.message;
  }

  return "Something went wrong. Please try again.";
}

function formatDateTime(timestamp: number) {
  return new Intl.DateTimeFormat("en-MY", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

function localDateTimeValue(timestamp: number) {
  const date = new Date(timestamp);
  const offset = date.getTimezoneOffset() * 60_000;

  return new Date(timestamp - offset)
    .toISOString()
    .slice(0, 16);
}

function startOfToday() {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function endOfToday() {
  const date = new Date();
  date.setHours(23, 59, 59, 999);
  return date.getTime();
}

function AppHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <header className="webapp-page-head">
      <div>
        <span>{eyebrow}</span>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>

      {action}
    </header>
  );
}

function LoadingState() {
  return (
    <section className="webapp-state glass-card">
      <div className="spinner" />
      <p>Synchronising with SARADIUM…</p>
    </section>
  );
}

function ErrorState({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <section className="message error">
      <strong>Unable to load this page.</strong>
      <p>{message}</p>

      {retry && (
        <button
          className="button secondary"
          type="button"
          onClick={retry}
        >
          Try again
        </button>
      )}
    </section>
  );
}

function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: string;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <section className="webapp-empty glass-card">
      <span
        className="webapp-empty-icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <h2>{title}</h2>
      <p>{text}</p>
      {action}
    </section>
  );
}

function Modal({
  title,
  close,
  children,
}: {
  title: string;
  close: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="webapp-modal-backdrop"
      role="presentation"
      onMouseDown={close}
    >
      <section
        className="webapp-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header>
          <h2>{title}</h2>

          <button
            type="button"
            onClick={close}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        {children}
      </section>
    </div>
  );
}

function TaskForm({
  routine,
  close,
  saved,
}: {
  routine: boolean;
  close: () => void;
  saved: () => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [icon, setIcon] = useState(
    routine ? "↻" : "◷",
  );

  const [dueAt, setDueAt] = useState(
    localDateTimeValue(
      Date.now() + 60 * 60 * 1000,
    ),
  );

  const [repeatRule, setRepeatRule] = useState(
    routine ? "DAILY" : "",
  );

  const [notificationsEnabled, setNotificationsEnabled] =
    useState(true);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setSaving(true);

    try {
      await createTask({
        title,
        note,
        icon,
        dueAt: new Date(dueAt).getTime(),
        routine,
        repeatRule:
          routine
            ? repeatRule || "DAILY"
            : repeatRule,
        notificationsEnabled,
      });

      await saved();
      close();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="webapp-form"
      onSubmit={submit}
    >
      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      <div className="webapp-form-row compact">
        <label>
          Icon
          <input
            value={icon}
            maxLength={8}
            onChange={(event) =>
              setIcon(event.target.value)
            }
          />
        </label>

        <label className="grow">
          Title
          <input
            value={title}
            maxLength={256}
            required
            autoFocus
            onChange={(event) =>
              setTitle(event.target.value)
            }
          />
        </label>
      </div>

      <label>
        Note
        <textarea
          value={note}
          rows={3}
          onChange={(event) =>
            setNote(event.target.value)
          }
        />
      </label>

      <label>
        Date and time
        <input
          type="datetime-local"
          value={dueAt}
          required
          onChange={(event) =>
            setDueAt(event.target.value)
          }
        />
      </label>

      {routine && (
        <label>
          Repeat
          <select
            value={repeatRule}
            onChange={(event) =>
              setRepeatRule(event.target.value)
            }
          >
            <option value="DAILY">Every day</option>
            <option value="WEEKDAYS">
              Weekdays
            </option>
            <option value="WEEKLY">
              Every week
            </option>
            <option value="MONTHLY">
              Every month
            </option>
          </select>
        </label>
      )}

      <label className="webapp-switch-row">
        <span>
          <strong>Notifications</strong>
          <small>
            Remind me when this item is due
          </small>
        </span>

        <input
          type="checkbox"
          checked={notificationsEnabled}
          onChange={(event) =>
            setNotificationsEnabled(
              event.target.checked,
            )
          }
        />
      </label>

      <footer className="webapp-form-actions">
        <button
          className="button secondary"
          type="button"
          onClick={close}
        >
          Cancel
        </button>

        <button
          className="button primary"
          type="submit"
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : routine
              ? "Create routine"
              : "Create reminder"}
        </button>
      </footer>
    </form>
  );
}

export function TodayPage() {
  const { user } = useAuth();

  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [countdowns, setCountdowns] =
    useState<CountdownRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  const firstName =
    user?.name?.trim().split(/\s+/)[0] ||
    "there";

  async function load() {
    setError("");
    setLoading(true);

    try {
      const [taskRows, countdownRows] =
        await Promise.all([
          listTasks(),
          listCountdowns(),
        ]);

      setTasks(taskRows);
      setCountdowns(countdownRows);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const todayTasks = useMemo(
    () =>
      tasks.filter(
        (task) =>
          task.dueAt >= startOfToday() &&
          task.dueAt <= endOfToday() &&
          !task.routine,
      ),
    [tasks],
  );

  const routines = useMemo(
    () => tasks.filter((task) => task.routine),
    [tasks],
  );

  const approachingCountdowns = useMemo(
    () =>
      countdowns
        .filter(
          (countdown) =>
            countdown.eventAt >= Date.now(),
        )
        .slice(0, 3),
    [countdowns],
  );

  if (loading) {
    return (
      <div className="webapp-page">
        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div className="webapp-page">
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      </div>
    );
  }

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="TODAY"
        title={`Good day, ${firstName}.`}
        description="Your reminders, routines and countdowns across every signed-in device."
        action={
          <div className="webapp-header-actions">
            <span className="webapp-date">
              {new Intl.DateTimeFormat("en-MY", {
                weekday: "long",
                day: "numeric",
                month: "long",
              }).format(new Date())}
            </span>

            <button
              className="button primary"
              type="button"
              onClick={() => setShowForm(true)}
            >
              Add reminder
            </button>
          </div>
        }
      />

      <section className="webapp-stats">
        <article>
          <span>Reminders</span>
          <strong>{todayTasks.length}</strong>
          <small>Due today</small>
        </article>

        <article>
          <span>Routines</span>
          <strong>{routines.length}</strong>
          <small>Active routines</small>
        </article>

        <article>
          <span>Countdowns</span>
          <strong>
            {approachingCountdowns.length}
          </strong>
          <small>Approaching</small>
        </article>
      </section>

      <div className="webapp-two-column">
        <section className="glass-card">
          <div className="webapp-section-title">
            <div>
              <span>TODAY’S REMINDERS</span>
              <h2>What matters next</h2>
            </div>
          </div>

          {todayTasks.length === 0 ? (
            <EmptyState
              icon="◷"
              title="A clear day"
              text="You have no reminders due today."
            />
          ) : (
            <div className="webapp-item-list">
              {todayTasks.map((task) => (
                <article
                  className={
                    task.completed
                      ? "webapp-item completed"
                      : "webapp-item"
                  }
                  key={task.$id}
                >
                  <button
                    className="webapp-check"
                    type="button"
                    aria-label={
                      task.completed
                        ? "Mark incomplete"
                        : "Mark complete"
                    }
                    onClick={async () => {
                      await setTaskCompleted(
                        task.$id,
                        !task.completed,
                      );

                      await load();
                    }}
                  >
                    {task.completed ? "✓" : ""}
                  </button>

                  <span className="webapp-item-icon">
                    {task.icon || "◷"}
                  </span>

                  <div>
                    <strong>{task.title}</strong>
                    <small>
                      {formatDateTime(task.dueAt)}
                    </small>

                    {task.note && <p>{task.note}</p>}
                  </div>

                  <button
                    className="webapp-delete"
                    type="button"
                    aria-label="Delete reminder"
                    onClick={async () => {
                      if (
                        window.confirm(
                          `Delete “${task.title}”?`,
                        )
                      ) {
                        await deleteTask(task.$id);
                        await load();
                      }
                    }}
                  >
                    ×
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>

        <section className="glass-card webapp-focus">
          <span>NEXT COUNTDOWNS</span>
          <h2>Moments approaching</h2>

          {approachingCountdowns.length === 0 ? (
            <p>
              Your next countdown will appear here.
            </p>
          ) : (
            <div className="webapp-mini-list">
              {approachingCountdowns.map(
                (countdown) => (
                  <article key={countdown.$id}>
                    <span>
                      {countdown.icon || "⌛"}
                    </span>

                    <div>
                      <strong>
                        {countdown.title}
                      </strong>
                      <small>
                        {formatDateTime(
                          countdown.eventAt,
                        )}
                      </small>
                    </div>
                  </article>
                ),
              )}
            </div>
          )}
        </section>
      </div>

      {showForm && (
        <Modal
          title="New reminder"
          close={() => setShowForm(false)}
        >
          <TaskForm
            routine={false}
            close={() => setShowForm(false)}
            saved={load}
          />
        </Modal>
      )}
    </div>
  );
}

export function RoutinesPage() {
  const [routines, setRoutines] =
    useState<TaskRow[]>([]);

  const [completions, setCompletions] =
    useState<RoutineCompletionRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    setError("");

    try {
      const [tasks, completionRows] =
        await Promise.all([
          listTasks(),
          listRoutineCompletions(),
        ]);

      setRoutines(
        tasks.filter((task) => task.routine),
      );

      setCompletions(completionRows);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function completed(taskId: string) {
    return completions.some(
      (row) =>
        row.taskRowId === taskId &&
        row.completed,
    );
  }

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="REPEAT WITH PURPOSE"
        title="Routines"
        description="Build consistency without making the day feel crowded."
        action={
          <button
            className="button primary"
            type="button"
            onClick={() => setShowForm(true)}
          >
            New routine
          </button>
        }
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      ) : routines.length === 0 ? (
        <EmptyState
          icon="↻"
          title="Create your first routine"
          text="Routine completion is recorded separately for every day."
          action={
            <button
              className="button primary"
              type="button"
              onClick={() => setShowForm(true)}
            >
              New routine
            </button>
          }
        />
      ) : (
        <section className="webapp-routine-grid">
          {routines.map((routine) => {
            const isCompleted =
              completed(routine.$id);

            return (
              <article
                className={
                  isCompleted
                    ? "glass-card routine-card completed"
                    : "glass-card routine-card"
                }
                key={routine.$id}
              >
                <div className="routine-top">
                  <span>{routine.icon || "↻"}</span>

                  <button
                    type="button"
                    onClick={async () => {
                      await setRoutineCompleted(
                        routine,
                        !isCompleted,
                      );

                      await load();
                    }}
                  >
                    {isCompleted
                      ? "Completed"
                      : "Mark done"}
                  </button>
                </div>

                <h2>{routine.title}</h2>

                <p>
                  {routine.note ||
                    "A routine worth repeating."}
                </p>

                <small>
                  {routine.repeatRule || "Daily"} ·{" "}
                  {formatDateTime(routine.dueAt)}
                </small>

                <button
                  className="text-link danger"
                  type="button"
                  onClick={async () => {
                    if (
                      window.confirm(
                        `Delete “${routine.title}”?`,
                      )
                    ) {
                      await deleteTask(routine.$id);
                      await load();
                    }
                  }}
                >
                  Delete routine
                </button>
              </article>
            );
          })}
        </section>
      )}

      {showForm && (
        <Modal
          title="New routine"
          close={() => setShowForm(false)}
        >
          <TaskForm
            routine
            close={() => setShowForm(false)}
            saved={load}
          />
        </Modal>
      )}
    </div>
  );
}

function remainingTime(eventAt: number) {
  const difference = Math.max(
    0,
    eventAt - Date.now(),
  );

  const seconds = Math.floor(
    difference / 1000,
  );

  const days = Math.floor(
    seconds / 86_400,
  );

  const hours = Math.floor(
    (seconds % 86_400) / 3_600,
  );

  const minutes = Math.floor(
    (seconds % 3_600) / 60,
  );

  const remainingSeconds = seconds % 60;

  return {
    finished: difference === 0,
    text: `${days}d ${String(hours).padStart(
      2,
      "0",
    )}h ${String(minutes).padStart(
      2,
      "0",
    )}m ${String(
      remainingSeconds,
    ).padStart(2, "0")}s`,
  };
}

function CountdownCard({
  countdown,
  remove,
}: {
  countdown: CountdownRow;
  remove: () => Promise<void>;
}) {
  const [, tick] = useState(0);

  useEffect(() => {
    const timer = window.setInterval(
      () => tick((value) => value + 1),
      1000,
    );

    return () => window.clearInterval(timer);
  }, []);

  const remaining = remainingTime(
    countdown.eventAt,
  );

  return (
    <article className="glass-card countdown-card">
      <div className="countdown-card-top">
        <span>{countdown.icon || "⌛"}</span>
        <small>{countdown.category}</small>
      </div>

      <h2>{countdown.title}</h2>

      <strong className="countdown-live">
        {remaining.finished
          ? "Reached"
          : remaining.text}
      </strong>

      <p>{formatDateTime(countdown.eventAt)}</p>

      {countdown.note && (
        <p>{countdown.note}</p>
      )}

      <button
        className="text-link danger"
        type="button"
        onClick={() => void remove()}
      >
        Delete countdown
      </button>
    </article>
  );
}

function CountdownForm({
  close,
  saved,
}: {
  close: () => void;
  saved: () => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [category, setCategory] =
    useState("Personal");

  const [icon, setIcon] = useState("⌛");
  const [note, setNote] = useState("");

  const [eventAt, setEventAt] = useState(
    localDateTimeValue(
      Date.now() + 24 * 60 * 60 * 1000,
    ),
  );

  const [journeyEnabled, setJourneyEnabled] =
    useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      await createCountdown({
        title,
        category,
        icon,
        note,
        eventAt: new Date(eventAt).getTime(),
        journeyEnabled,
      });

      await saved();
      close();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="webapp-form"
      onSubmit={submit}
    >
      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      <div className="webapp-form-row compact">
        <label>
          Icon
          <input
            value={icon}
            maxLength={8}
            onChange={(event) =>
              setIcon(event.target.value)
            }
          />
        </label>

        <label className="grow">
          Title
          <input
            value={title}
            required
            autoFocus
            maxLength={256}
            onChange={(event) =>
              setTitle(event.target.value)
            }
          />
        </label>
      </div>

      <label>
        Category
        <input
          value={category}
          maxLength={128}
          onChange={(event) =>
            setCategory(event.target.value)
          }
        />
      </label>

      <label>
        Target date and time
        <input
          type="datetime-local"
          value={eventAt}
          required
          onChange={(event) =>
            setEventAt(event.target.value)
          }
        />
      </label>

      <label>
        Note
        <textarea
          value={note}
          rows={3}
          onChange={(event) =>
            setNote(event.target.value)
          }
        />
      </label>

      <label className="webapp-switch-row">
        <span>
          <strong>Event Journey</strong>
          <small>
            Follow this countdown as a journey
          </small>
        </span>

        <input
          type="checkbox"
          checked={journeyEnabled}
          onChange={(event) =>
            setJourneyEnabled(
              event.target.checked,
            )
          }
        />
      </label>

      <footer className="webapp-form-actions">
        <button
          className="button secondary"
          type="button"
          onClick={close}
        >
          Cancel
        </button>

        <button
          className="button primary"
          type="submit"
          disabled={saving}
        >
          {saving
            ? "Saving…"
            : "Create countdown"}
        </button>
      </footer>
    </form>
  );
}

export function CountdownsPage() {
  const [countdowns, setCountdowns] =
    useState<CountdownRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    setError("");

    try {
      setCountdowns(await listCountdowns());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="LOOK FORWARD"
        title="Countdowns"
        description="See important moments approaching in real time."
        action={
          <button
            className="button primary"
            type="button"
            onClick={() => setShowForm(true)}
          >
            New countdown
          </button>
        }
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      ) : countdowns.length === 0 ? (
        <EmptyState
          icon="⌛"
          title="Nothing counting down yet"
          text="Create a countdown and it will synchronise with your SARADIUM account."
        />
      ) : (
        <section className="webapp-card-grid">
          {countdowns.map((countdown) => (
            <CountdownCard
              key={countdown.$id}
              countdown={countdown}
              remove={async () => {
                if (
                  window.confirm(
                    `Delete “${countdown.title}”?`,
                  )
                ) {
                  await deleteCountdown(
                    countdown.$id,
                  );

                  await load();
                }
              }}
            />
          ))}
        </section>
      )}

      {showForm && (
        <Modal
          title="New countdown"
          close={() => setShowForm(false)}
        >
          <CountdownForm
            close={() => setShowForm(false)}
            saved={load}
          />
        </Modal>
      )}
    </div>
  );
}

function EventForm({
  close,
  saved,
}: {
  close: () => void;
  saved: () => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [category, setCategory] =
    useState("Personal");

  const [startAt, setStartAt] = useState(
    localDateTimeValue(
      Date.now() + 60 * 60 * 1000,
    ),
  );

  const [endAt, setEndAt] = useState(
    localDateTimeValue(
      Date.now() + 2 * 60 * 60 * 1000,
    ),
  );

  const [location, setLocation] =
    useState("");

  const [notes, setNotes] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      await createEvent({
        title,
        category,
        startAt: new Date(startAt).getTime(),
        endAt: new Date(endAt).getTime(),
        location,
        notes,
        allDay,
      });

      await saved();
      close();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <form
      className="webapp-form"
      onSubmit={submit}
    >
      {error && (
        <div className="message error">
          {error}
        </div>
      )}

      <label>
        Event title
        <input
          value={title}
          required
          autoFocus
          maxLength={256}
          onChange={(event) =>
            setTitle(event.target.value)
          }
        />
      </label>

      <label>
        Category
        <input
          value={category}
          maxLength={128}
          onChange={(event) =>
            setCategory(event.target.value)
          }
        />
      </label>

      <div className="webapp-form-row">
        <label>
          Starts
          <input
            type="datetime-local"
            value={startAt}
            required
            onChange={(event) =>
              setStartAt(event.target.value)
            }
          />
        </label>

        <label>
          Ends
          <input
            type="datetime-local"
            value={endAt}
            required
            onChange={(event) =>
              setEndAt(event.target.value)
            }
          />
        </label>
      </div>

      <label>
        Location
        <input
          value={location}
          maxLength={1000}
          onChange={(event) =>
            setLocation(event.target.value)
          }
        />
      </label>

      <label>
        Notes
        <textarea
          value={notes}
          rows={3}
          onChange={(event) =>
            setNotes(event.target.value)
          }
        />
      </label>

      <label className="webapp-switch-row">
        <span>
          <strong>All-day event</strong>
          <small>
            Hide the specific event time
          </small>
        </span>

        <input
          type="checkbox"
          checked={allDay}
          onChange={(event) =>
            setAllDay(event.target.checked)
          }
        />
      </label>

      <footer className="webapp-form-actions">
        <button
          className="button secondary"
          type="button"
          onClick={close}
        >
          Cancel
        </button>

        <button
          className="button primary"
          type="submit"
          disabled={saving}
        >
          {saving ? "Saving…" : "Create event"}
        </button>
      </footer>
    </form>
  );
}

export function CalendarPage() {
  const [events, setEvents] =
    useState<EventRow[]>([]);

  const [selectedDate, setSelectedDate] =
    useState(new Date());

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);

  async function load() {
    setLoading(true);
    setError("");

    try {
      setEvents(await listEvents());
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();

  const days = useMemo(() => {
    const firstWeekday = new Date(
      year,
      month,
      1,
    ).getDay();

    const daysInMonth = new Date(
      year,
      month + 1,
      0,
    ).getDate();

    const cells: Array<number | null> = [
      ...Array(firstWeekday).fill(null),
    ];

    for (
      let day = 1;
      day <= daysInMonth;
      day += 1
    ) {
      cells.push(day);
    }

    return cells;
  }, [year, month]);

  function eventsForDay(day: number) {
    return events.filter((event) => {
      const eventDate = new Date(event.startAt);

      return (
        eventDate.getFullYear() === year &&
        eventDate.getMonth() === month &&
        eventDate.getDate() === day
      );
    });
  }

  function changeMonth(change: number) {
    setSelectedDate(
      new Date(year, month + change, 1),
    );
  }

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="YOUR TIME"
        title="Calendar"
        description="Reminders, personal events and Malaysian dates in one place."
        action={
          <button
            className="button primary"
            type="button"
            onClick={() => setShowForm(true)}
          >
            New event
          </button>
        }
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      ) : (
        <section className="glass-card webapp-calendar">
          <header>
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              aria-label="Previous month"
            >
              ‹
            </button>

            <h2>
              {new Intl.DateTimeFormat("en-MY", {
                month: "long",
                year: "numeric",
              }).format(selectedDate)}
            </h2>

            <button
              type="button"
              onClick={() => changeMonth(1)}
              aria-label="Next month"
            >
              ›
            </button>
          </header>

          <div className="calendar-weekdays">
            {[
              "Sun",
              "Mon",
              "Tue",
              "Wed",
              "Thu",
              "Fri",
              "Sat",
            ].map((day) => (
              <span key={day}>{day}</span>
            ))}
          </div>

          <div className="calendar-grid">
            {days.map((day, index) => {
              if (day === null) {
                return (
                  <span
                    className="calendar-cell empty"
                    key={`empty-${index}`}
                  />
                );
              }

              const dayEvents =
                eventsForDay(day);

              const today = new Date();

              const isToday =
                today.getFullYear() === year &&
                today.getMonth() === month &&
                today.getDate() === day;

              return (
                <article
                  className={
                    isToday
                      ? "calendar-cell today"
                      : "calendar-cell"
                  }
                  key={day}
                >
                  <strong>{day}</strong>

                  {dayEvents.slice(0, 2).map(
                    (event) => (
                      <span
                        className="calendar-event"
                        key={event.$id}
                        title={event.title}
                      >
                        {event.title}
                      </span>
                    ),
                  )}

                  {dayEvents.length > 2 && (
                    <small>
                      +{dayEvents.length - 2} more
                    </small>
                  )}
                </article>
              );
            })}
          </div>

          <div className="calendar-agenda">
            <h3>Upcoming events</h3>

            {events.length === 0 ? (
              <p className="muted">
                No personal events yet.
              </p>
            ) : (
              events
                .filter(
                  (event) =>
                    event.endAt >= Date.now(),
                )
                .slice(0, 10)
                .map((event) => (
                  <article key={event.$id}>
                    <div>
                      <strong>{event.title}</strong>
                      <small>
                        {formatDateTime(
                          event.startAt,
                        )}
                      </small>

                      {event.location && (
                        <p>{event.location}</p>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={async () => {
                        if (
                          window.confirm(
                            `Delete “${event.title}”?`,
                          )
                        ) {
                          await deleteEvent(event.$id);
                          await load();
                        }
                      }}
                    >
                      Delete
                    </button>
                  </article>
                ))
            )}
          </div>
        </section>
      )}

      {showForm && (
        <Modal
          title="New event"
          close={() => setShowForm(false)}
        >
          <EventForm
            close={() => setShowForm(false)}
            saved={load}
          />
        </Modal>
      )}
    </div>
  );
}

export function EventCirclePage() {
  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="SHARED MOMENTS"
        title="Event Circle"
        description="Plan and follow important events together."
      />

      <section className="message">
        <strong>
          Secure Event Circle connection is next.
        </strong>

        <p>
          The tables are ready, but circle creation,
          invitations and membership changes must go
          through the SARADIUM Backend Function. They
          will not be written directly from the browser.
        </p>
      </section>
    </div>
  );
}

export function WebProfilePage() {
  const { user } = useAuth();

  const [profile, setProfile] =
    useState<ProfileIdentity | null>(null);

  const [entitlement, setEntitlement] =
    useState<EntitlementRow | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    setError("");

    try {
      const [identity, plan] =
        await Promise.all([
          getMyProfile(),
          getMyEntitlement(),
        ]);

      setProfile(identity);
      setEntitlement(plan);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="SARADIUM IDENTITY"
        title={
          profile?.displayName ||
          user?.name ||
          "Your profile"
        }
        description="Your identity follows you across Android, iPhone and desktop."
      />

      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      ) : (
        <>
          <section className="webapp-profile-card glass-card">
            <div className="webapp-avatar">
              {(
                profile?.displayName ||
                user?.name ||
                "S"
              )
                .charAt(0)
                .toUpperCase()}
            </div>

            <div>
              <h2>
                {profile?.displayName ||
                  user?.name ||
                  "SARADIUM user"}
              </h2>

              {profile?.username && (
                <strong className="profile-username">
                  @{profile.username}
                </strong>
              )}

              <p>{user?.email}</p>

              <div className="webapp-pills">
                <span>
                  {user?.emailVerification
                    ? "Email verified"
                    : "Verification pending"}
                </span>

                <span>
                  SARADIUM{" "}
                  {entitlement?.plan || "FREE"}
                </span>

                {profile?.badgeNumber ? (
                  <span>
                    Badge {profile.badgeNumber}
                  </span>
                ) : null}

                {profile?.owner && (
                  <span className="owner-pill">
                    Owner
                  </span>
                )}
              </div>
            </div>
          </section>

          <div className="webapp-two-column">
            <section className="glass-card">
              <span>SUBSCRIPTION</span>
              <h3>
                {entitlement?.plan || "FREE"}
              </h3>

              <p>
                Status:{" "}
                {entitlement?.subscriptionState ||
                  "NONE"}
              </p>

              <Link
                className="button secondary"
                to="/pricing"
              >
                View plans
              </Link>
            </section>

            <section className="glass-card">
              <span>ACCOUNT</span>
              <h3>Security and privacy</h3>

              <p>
                Appwrite manages your password and
                session. SARADIUM never displays or
                stores your password.
              </p>

              <Link
                className="button secondary"
                to="/account"
              >
                Manage account
              </Link>
            </section>
          </div>
        </>
      )}
    </div>
  );
}

export function WebSettingsPage() {
  const [preferences, setPreferences] =
    useState<UserPreferencesRow | null>(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [feedbackCategory, setFeedbackCategory] =
    useState("GENERAL");

  const [feedbackMessage, setFeedbackMessage] =
    useState("");

  async function load() {
    setLoading(true);
    setError("");

    try {
      const result = await getPreferences();

      setPreferences(
        result ?? {
          $id: "",
          $createdAt: "",
          $updatedAt: "",
          userId: "",
          theme: "SYSTEM",
          style: "LIQUID_GLASS",
          myState: "Penang",
          timeZone:
            Intl.DateTimeFormat()
              .resolvedOptions()
              .timeZone,
          notificationsEnabled: true,
          updatedAt: Date.now(),
        },
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function save() {
    if (!preferences) {
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const result = await savePreferences({
        theme: preferences.theme,
        style: preferences.style,
        myState: preferences.myState,
        timeZone: preferences.timeZone,
        notificationsEnabled:
          preferences.notificationsEnabled,
      });

      setPreferences(result);
      setSuccess("Settings saved.");
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  async function sendFeedback(
    event: FormEvent,
  ) {
    event.preventDefault();
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      await submitFeedback(
        feedbackCategory,
        feedbackMessage,
      );

      setFeedbackMessage("");
      setSuccess(
        "Thank you. Your feedback was submitted.",
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="webapp-page">
      <AppHeader
        eyebrow="YOUR EXPERIENCE"
        title="Settings"
        description="Control appearance, notifications, synchronisation and privacy."
      />

      {loading ? (
        <LoadingState />
      ) : error && !preferences ? (
        <ErrorState
          message={error}
          retry={() => void load()}
        />
      ) : preferences ? (
        <>
          {error && (
            <div className="message error">
              {error}
            </div>
          )}

          {success && (
            <div className="message success">
              {success}
            </div>
          )}

          <div className="webapp-settings-list">
            <section className="glass-card">
              <h3>Theme</h3>

              <select
                value={preferences.theme}
                onChange={(event) =>
                  setPreferences({
                    ...preferences,
                    theme: event.target
                      .value as UserPreferencesRow["theme"],
                  })
                }
              >
                <option value="SYSTEM">
                  Use device setting
                </option>

                <option value="LIGHT">
                  Light
                </option>

                <option value="DARK">
                  Dark
                </option>
              </select>
            </section>

            <section className="glass-card">
              <h3>Interface style</h3>

              <select
                value={preferences.style}
                onChange={(event) =>
                  setPreferences({
                    ...preferences,
                    style: event.target
                      .value as UserPreferencesRow["style"],
                  })
                }
              >
                <option value="LIQUID_GLASS">
                  Liquid Glass
                </option>

                <option value="SPATIAL">
                  Spatial
                </option>

                <option value="MINIMAL">
                  Minimal
                </option>
              </select>
            </section>

            <section className="glass-card">
              <h3>Malaysia calendar</h3>

              <label>
                My state
                <select
                  value={preferences.myState}
                  onChange={(event) =>
                    setPreferences({
                      ...preferences,
                      myState: event.target.value,
                    })
                  }
                >
                  {[
                    "All Malaysia",
                    "Johor",
                    "Kedah",
                    "Kelantan",
                    "Melaka",
                    "Negeri Sembilan",
                    "Pahang",
                    "Penang",
                    "Perak",
                    "Perlis",
                    "Sabah",
                    "Sarawak",
                    "Selangor",
                    "Terengganu",
                    "Kuala Lumpur",
                    "Labuan",
                    "Putrajaya",
                  ].map((state) => (
                    <option
                      value={state}
                      key={state}
                    >
                      {state}
                    </option>
                  ))}
                </select>
              </label>
            </section>

            <section className="glass-card">
              <h3>Notifications</h3>

              <label className="webapp-switch-row">
                <span>
                  <strong>
                    Browser notifications
                  </strong>

                  <small>
                    Keep reminder notifications on
                  </small>
                </span>

                <input
                  type="checkbox"
                  checked={
                    preferences.notificationsEnabled
                  }
                  onChange={(event) =>
                    setPreferences({
                      ...preferences,
                      notificationsEnabled:
                        event.target.checked,
                    })
                  }
                />
              </label>
            </section>
          </div>

          <button
            className="button primary"
            type="button"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving
              ? "Saving…"
              : "Save settings"}
          </button>

          <section className="glass-card webapp-feedback">
            <span>FEEDBACK</span>
            <h2>Help improve SARADIUM</h2>

            <form
              className="webapp-form"
              onSubmit={sendFeedback}
            >
              <label>
                Category
                <select
                  value={feedbackCategory}
                  onChange={(event) =>
                    setFeedbackCategory(
                      event.target.value,
                    )
                  }
                >
                  <option value="GENERAL">
                    General feedback
                  </option>

                  <option value="FEATURE">
                    Feature request
                  </option>

                  <option value="BUG">
                    Report a problem
                  </option>

                  <option value="PRIVACY">
                    Privacy request
                  </option>
                </select>
              </label>

              <label>
                Message
                <textarea
                  value={feedbackMessage}
                  required
                  rows={5}
                  maxLength={5000}
                  onChange={(event) =>
                    setFeedbackMessage(
                      event.target.value,
                    )
                  }
                />
              </label>

              <button
                className="button secondary"
                type="submit"
                disabled={
                  saving ||
                  !feedbackMessage.trim()
                }
              >
                Submit feedback
              </button>
            </form>
          </section>
        </>
      ) : null}
    </div>
  );
}